<template>
  <div class="relative right-0 h-content-window overflow-hidden">
    <div class="w-full h-full bg-white overflow-auto p-4">
      <div class="max-w-3xl mx-auto">
        <!-- Header -->
        <div class="mb-6 text-center">
          <h1 class="font-trebuchet-pixel text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {{ $t('windows.tourGuide.title') }}
          </h1>
          <p class="text-sm text-gray-600">
            {{ $t('windows.tourGuide.subtitle') }}
          </p>
        </div>

        <!-- Progress Bar -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs text-gray-600">
              {{ $t('windows.tourGuide.step') }} {{ currentStep + 1 }} / {{ steps.length }}
            </span>
            <span class="text-xs text-gray-600">
              {{ Math.round(((currentStep + 1) / steps.length) * 100) }}%
            </span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-600 h-2 rounded-full transition-all duration-300"
              :style="{ width: `${((currentStep + 1) / steps.length) * 100}%` }"
            ></div>
          </div>
        </div>

        <!-- Current Step Content -->
        <div class="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
          <div class="flex items-start gap-4 mb-4">
            <div class="flex-shrink-0">
              <img
                :src="steps[currentStep].icon"
                :alt="steps[currentStep].title"
                class="w-16 h-16 object-contain"
              />
            </div>
            <div class="flex-1">
              <h2 class="font-trebuchet-pixel text-xl font-semibold text-gray-900 mb-2">
                {{ steps[currentStep].title }}
              </h2>
              <p class="text-sm text-gray-700 leading-relaxed mb-3">
                {{ steps[currentStep].description }}
              </p>
              <div v-if="steps[currentStep].features && steps[currentStep].features.length > 0" class="mt-3">
                <ul class="list-disc list-inside text-xs text-gray-600 space-y-1">
                  <li v-for="feature in steps[currentStep].features" :key="feature">
                    {{ feature }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="flex items-center justify-between gap-4">
          <button
            @click="previousStep"
            :disabled="currentStep === 0"
            class="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-trebuchet-pixel text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {{ $t('windows.tourGuide.previous') }}
          </button>

          <div class="flex gap-2">
            <button
              v-if="steps[currentStep].windowId"
              @click="openWindow"
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-trebuchet-pixel text-sm"
            >
              {{ $t('windows.tourGuide.openWindow') }}
            </button>
            <button
              v-if="currentStep < steps.length - 1"
              @click="nextStep"
              class="next-button flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-trebuchet-pixel text-sm"
            >
              {{ $t('windows.tourGuide.next') }}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            <button
              v-else
              @click="finishTour"
              class="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-trebuchet-pixel text-sm"
            >
              {{ $t('windows.tourGuide.finish') }}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const localeStore = useLocaleStore()

const emit = defineEmits(['open-window', 'close-tour'])

const currentStep = ref(0)

const steps = computed(() => {
  const locale = localeStore.currentLocale
  return [
    {
      title: t('windows.tourGuide.steps.about.title'),
      description: t('windows.tourGuide.steps.about.description'),
      icon: '/img/icons/about/about-icon-sm.webp',
      windowId: 'about',
      features: [
        t('windows.tourGuide.steps.about.feature1'),
        t('windows.tourGuide.steps.about.feature2'),
        t('windows.tourGuide.steps.about.feature3')
      ]
    },
    {
      title: t('windows.tourGuide.steps.academicPath.title'),
      description: t('windows.tourGuide.steps.academicPath.description'),
      icon: '/img/icons/cv/cv-icon-sm.webp',
      windowId: 'academicPath',
      features: [
        t('windows.tourGuide.steps.academicPath.feature1'),
        t('windows.tourGuide.steps.academicPath.feature2')
      ]
    },
    {
      title: t('windows.tourGuide.steps.competences.title'),
      description: t('windows.tourGuide.steps.competences.description'),
      icon: '/img/icons/skills/skills-icon-sm.webp',
      windowId: 'competences',
      features: [
        t('windows.tourGuide.steps.competences.feature1'),
        t('windows.tourGuide.steps.competences.feature2')
      ]
    },
    {
      title: t('windows.tourGuide.steps.activites.title'),
      description: t('windows.tourGuide.steps.activites.description'),
      icon: '/img/icons/activities/activities-icon-sm.webp',
      windowId: 'activites',
      features: [
        t('windows.tourGuide.steps.activites.feature1'),
        t('windows.tourGuide.steps.activites.feature2')
      ]
    },
    {
      title: t('windows.tourGuide.steps.reseau.title'),
      description: t('windows.tourGuide.steps.reseau.description'),
      icon: '/img/icons/network/network-icon-sm.webp',
      windowId: 'reseau',
      features: [
        t('windows.tourGuide.steps.reseau.feature1'),
        t('windows.tourGuide.steps.reseau.feature2')
      ]
    },
    {
      title: t('windows.tourGuide.steps.contact.title'),
      description: t('windows.tourGuide.steps.contact.description'),
      icon: '/img/icons/contact/email-icon-sm.webp',
      windowId: 'contact',
      features: [
        t('windows.tourGuide.steps.contact.feature1'),
        t('windows.tourGuide.steps.contact.feature2')
      ]
    }
  ]
})

const nextStep = () => {
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const openWindow = () => {
  if (steps.value[currentStep.value].windowId) {
    emit('open-window', steps.value[currentStep.value].windowId)
  }
}

const finishTour = () => {
  emit('close-tour')
}
</script>

<style scoped>
/* Animation de pulsation pour le bouton suivant */
.next-button {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(37, 99, 235, 0);
  }
}

.next-button:hover {
  animation: none;
  transform: scale(1.05);
}
</style>

